import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { lastValueFrom } from 'rxjs';
import html2pdf from 'html2pdf.js';
import { BaseHttpService } from '../auth/base-capacitor-http';
import { AuthResponse } from '../auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { environment } from '../../environments/environment';

export class Receipt {
  email: string;
  ref: string;
  orderId: number;
  status: string;
  shipping: number;
  orderDate: Date;
  name: string;
  items: OrderItem[];
  periodStart: number;
  periodEnd: number;
  effectiveAt: number;
  number: string;
  summary: OrderSummary;
}
export class OrderSummary{
  subtotal: number;
  total: number;
  discount: number;
  tax: number;

}

export class OrderItem {
  quantity: number;
  shipped: number;
  product: ProductDisplay;
  price: number;
  url: string;

}

export class SubData {
  active: boolean;
  expires: number;

}

export class Subscription {
  id: string;
  date: Date;
  status: string;
  paymentMethod: string;
  items: SubItem[];
  total: number;
}

export class SubItem {
  name: string;
  quantity: number;
  price: number;
}
export class CheckoutSession {
  id: string;
  sid?: string;
  line_items: CheckoutLineItem[];
  mode: 'payment';
  success_url: string;
  cancel_url: string;
  url: string;
}

export class CheckoutLineItem {
  currency: 'usd';
  price_id: string;
  description: string;
  name: string;
  images: string[]
  unit_amount: number;
  quantity: number;
}

export class ProductDisplay {
  name: string;
  description: string;
  amount: number;
  interval: string;
  currency: string;
  status: string;
  type: string;
  price_id: string;
  img: string;
  images: string[];
  features: string[];
}



@Injectable({
  providedIn: 'root'
})
export class PaymentService extends BaseHttpService {
  private readonly PROMO_KEY = 'PROMO';
  private baseUrl: string;
  private urlEndPoint: string;

  constructor(client: HttpClient, snackBar: MatSnackBar) {
    super(client, snackBar);
    this.baseUrl = environment.server.mainURL;
  }

  private async getStripePk(): Promise<{ pk: string }> {
    this.urlEndPoint = this.baseUrl + 'finance/pk';
    return this.get<{ pk: string }>(this.urlEndPoint);
  }

  async addPromoCode(promo: string) {
    if (Capacitor.isNativePlatform()) {
      try {
        await SecureStoragePlugin.set({ key: this.PROMO_KEY, value: promo });
      } catch {
        return null;
      }
      return;
    }
    const tokenResponse: string = this.encryptPayLoad<string>(promo, this.KEY);
    await Preferences.set({ key: this.AUTH_TOKEN, value: tokenResponse });
  }

  async getPromo() {
    if (Capacitor.isNativePlatform()) {
      try {
        const response = await SecureStoragePlugin.get({ key: this.PROMO });
        return response.value;
      } catch (err) {
        return null;
      }
    }

    const { value } = await Preferences.get({ key: this.AUTH_TOKEN });
    if (value) {
      let response = this.decryptPayload<string>(value, this.KEY);
      return response;
    }
    return null;
  }

  async removePromo() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: this.PROMO_KEY });
      return;
    }
    await Preferences.remove({ key: this.PROMO_KEY });
  }



  async getCheckoutSession(product: ProductDisplay) {

    this.urlEndPoint = this.baseUrl + 'finance/checkout';
    
    let body = {
      success_url: environment.server.domain + 'success?session_id={CHECKOUT_SESSION_ID}',// as per documentation
      cancel_url: environment.server.domain + 'cart-view',
      price_id: product.price_id,
      promo: '',// await this.getPromo(),
      name: product.name,
      url: product.img
    }
    return this.post<CheckoutSession>(this.urlEndPoint, body);
  }

  async checkout(url: string) {
    window.location.href = url;
  }

  async openCustomerPortal() {
    const res = await this.customerPortal();
    window.location.href = res.url;
  }

  private async customerPortal() {
    this.urlEndPoint = this.baseUrl + 'finance/billing-portal';
    let body = {
      returnUrl: environment.server.domain + 'settings'
    };

    return this.post<{ url: string }>(this.urlEndPoint, body);
  }


  downLoadReceipt(element: ElementRef) {


    const options: any = {
      margin: 10,
      filename: 'receipt.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true, // Important if you have images or fonts from external sources
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      }
    };

    // Ensure the element exists
    if (!element?.nativeElement) {
      console.error('ElementRef is invalid or not found');
      return;
    }

    html2pdf()
      .set(options)
      .from(element.nativeElement)
      .save()
      .catch(err => {
        console.error('PDF generation error:', err);
      });
  }

  async emailPdf(element: ElementRef) {
    const options: any = {
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const worker = html2pdf()
      .set(options)
      .from(element.nativeElement);

    const blob: Blob = await worker.outputPdf('blob');
    const formData = new FormData();
    formData.append('file', blob, 'receipt.pdf');

    this.urlEndPoint = this.baseUrl + 'finance/email-receipt'
    const headers = await this.getAuthHeaders();
    if (Capacitor.isNativePlatform()) {
      const res = await CapacitorHttp.post({ url: this.urlEndPoint, data: formData, headers: headers });
      return res.data as boolean;
    }

    return lastValueFrom(this.client.post<boolean>(this.urlEndPoint, formData, { headers: new HttpHeaders(headers) }));

  }

  async getUserPayHistory() {
    this.urlEndPoint = this.baseUrl + 'finance/receipt';
    return this.get<Subscription>(this.urlEndPoint);
  }

  async getReceipt(sessionId: string) {
    this.urlEndPoint = this.baseUrl + 'finance/session-receipt';
    return this.get<Receipt>(this.urlEndPoint, { session_id: sessionId });
  }

  async checkForActiveSubscription() {
    this.urlEndPoint = this.baseUrl + 'finance/check-subscription';
    return this.get<AuthResponse>(this.urlEndPoint);
  }

  async getProducts(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseUrl + 'commons/product-info';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }

  async cancelCustomerActiveSubscription(subId: string, type: string) {

    this.urlEndPoint = this.baseUrl + 'finance/subscription/' + subId;
    let body = {
      type: type,
    };

    return this.put<Subscription>(this.urlEndPoint, body);
  }

  async getCustomerActiveSubscription(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseUrl + 'finance/subscription-all';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }


}
